// The sections a path can sit in. A section names both a page's fallback
// title subject (`page-title.ts`) and its GA4 page_type and page_id param
// (`analytics/page-meta.ts`): one table, so a page's title and its page_type
// cannot name different sections.
//
// Section subjects are English on purpose, whatever the viewer's locale. The
// title is an analytics dimension as much as a tab label, and a localised one
// would split every page into one row per language.

import circlesCopy from '../../messages/en/circles.json';
import invitationsCopy from '../../messages/en/invitations.json';
import networksCopy from '../../messages/en/networks.json';
import notificationsCopy from '../../messages/en/notifications.json';
import resourcesCopy from '../../messages/en/resources.json';
import searchCopy from '../../messages/en/search.json';
import shellCopy from '../../messages/en/shell.json';

// The GA4 `page_type` of a section (`resolvePageMeta`); a page outside every
// typed section reports 'other'. Documented in docs/analytics/ga4-events.md,
// where a new value has to be added too: it is a reporting dimension.
export type Ga4PageType =
  | 'circle'
  | 'network'
  | 'group'
  | 'event'
  | 'resource'
  | 'learning_track'
  | 'profile'
  | 'feed'
  | 'people'
  | 'onboarding'
  | 'settings'
  | 'messages'
  | 'other';

// A typed section names the route param its detail pages carry the entity id
// in, for the GA4 `page_id`, or null when the URL never carries a reportable
// id. A profile URL carries the member's handle, which is derived from their
// name and so may not reach GA4: its page_id is the uuid its loader supplies.
export type PageSection =
  | { readonly prefix: string; readonly subject: string; readonly pageType?: never }
  | {
      readonly prefix: string;
      readonly subject: string;
      readonly pageType: Exclude<Ga4PageType, 'other'>;
      readonly pageIdParam: 'id' | 'trackId' | null;
    };

// Every subject here is product copy, never member-authored text. The title
// reaches GA4 on every event (gtag reads `document.title`), and Google's
// terms forbid sending it anything that could identify a person, so a page
// about a member or a member's post is titled by its section, not its
// subject. Entity names reach the title only through a route loader's
// `pageTitle`, and only on the editorial routes that return one: resources and
// learning tracks.
//
// The fixed public pages can also return a `pageTitle`, but it is not an
// entity name: it is the search title an editor wrote for that page
// (`cachedStaticPageTitle`), which is product copy like the subjects below.
//
// Matched by whole path segment, so `/join` does not claim `/join-a-circle`.
// The longest matching prefix wins, so a section nested in another (the
// learning tracks under `/resources`) claims its paths wherever it is listed.
const SECTIONS: ReadonlyArray<PageSection> = [
  { prefix: '/admin', subject: shellCopy.nav.adminPanel },
  { prefix: '/circles', subject: circlesCopy.chrome.title, pageType: 'circle', pageIdParam: 'id' },
  { prefix: '/coach', subject: shellCopy.nav.careerCoach },
  { prefix: '/community-guidelines', subject: 'Community Guidelines' },
  { prefix: '/events', subject: shellCopy.nav.events, pageType: 'event', pageIdParam: 'id' },
  { prefix: '/feed', subject: shellCopy.nav.home, pageType: 'feed', pageIdParam: null },
  { prefix: '/groups', subject: shellCopy.nav.groups, pageType: 'group', pageIdParam: 'id' },
  { prefix: '/help', subject: shellCopy.nav.help },
  { prefix: '/invitations', subject: invitationsCopy.page.title },
  { prefix: '/join-a-circle', subject: 'Join a Circle' },
  { prefix: '/join-a-network', subject: 'Join a Network' },
  { prefix: '/join', subject: 'Join Lean In' },
  { prefix: '/members', subject: 'Members', pageType: 'profile', pageIdParam: null },
  { prefix: '/messages', subject: shellCopy.nav.messages, pageType: 'messages', pageIdParam: null },
  { prefix: '/moderation', subject: shellCopy.nav.moderation },
  { prefix: '/networks', subject: networksCopy.chrome.title, pageType: 'network', pageIdParam: 'id' },
  { prefix: '/notifications', subject: notificationsCopy.title },
  { prefix: '/onboarding', subject: 'Onboarding', pageType: 'onboarding', pageIdParam: null },
  { prefix: '/people', subject: shellCopy.nav.people, pageType: 'people', pageIdParam: null },
  { prefix: '/posts', subject: 'Posts' },
  { prefix: '/privacy', subject: 'Privacy Policy' },
  { prefix: '/profile', subject: 'Profile' },
  {
    prefix: '/resources',
    subject: resourcesCopy.list.title,
    pageType: 'resource',
    pageIdParam: 'id',
  },
  {
    prefix: '/resources/learning-tracks',
    subject: resourcesCopy.list.title,
    pageType: 'learning_track',
    pageIdParam: 'trackId',
  },
  { prefix: '/roadmap', subject: shellCopy.nav.roadmap },
  { prefix: '/search', subject: searchCopy.results.title },
  { prefix: '/settings', subject: shellCopy.nav.settings, pageType: 'settings', pageIdParam: null },
  { prefix: '/start-a-circle', subject: 'Start a Circle' },
  { prefix: '/terms', subject: 'Terms of Service' },
];

// The section a path belongs to, or null for a path that is only the brand
// (auth, invite links).
export function pageSection(pathname: string): PageSection | null {
  let match: PageSection | null = null;
  for (const section of SECTIONS) {
    const { prefix } = section;
    const claims = pathname === prefix || pathname.startsWith(`${prefix}/`);
    if (claims && (match === null || prefix.length > match.prefix.length)) match = section;
  }
  return match;
}

